import { useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import { Button, Field, Input } from '@mgdlead/ui';

import { AuthLayout } from '../../components/AuthLayout';
import { Checkbox } from '@mgdlead/ui';
import { FormAlert } from '@mgdlead/ui';
import { readNextPath } from '../../auth/guards';
import { signInWithPassword } from '../../lib/auth';
import { loginSchema } from '../../lib/schemas';
import type { LoginValues } from '../../lib/schemas';
import { useDocumentTitle } from '../../lib/useDocumentTitle';
import { useZodForm } from '../../lib/useZodForm';

const INITIAL: LoginValues = { email: '', password: '', rememberMe: true };

export function LoginPage() {
  useDocumentTitle('Entrar');
  const navigate = useNavigate();
  const location = useLocation();

  // o destino guardado pela guarda de rota: o usuário volta pra onde queria ir
  const next = readNextPath(location.search);

  const onSubmit = useCallback(
    async (values: { email: string; password: string; rememberMe: boolean }) => {
      await signInWithPassword(values);
      void navigate(next ?? '/', { replace: true });
    },
    [navigate, next],
  );

  const form = useZodForm({ schema: loginSchema, initialValues: INITIAL, onSubmit });

  return (
    <AuthLayout
      title="Entrar no painel"
      description="Use o e-mail cadastrado no seu workspace."
      footer={
        <>
          Ainda não tem conta?{' '}
          <Link
            to={next ? `/cadastro?next=${encodeURIComponent(next)}` : '/cadastro'}
            className="font-medium text-primary-text underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            Criar conta
          </Link>
        </>
      }
    >
      <form noValidate onSubmit={form.handleSubmit} className="flex flex-col gap-4">
        {form.formError ? <FormAlert>{form.formError}</FormAlert> : null}

        <Field label="E-mail" error={form.errors.email} required>
          {(control) => (
            <Input
              {...control}
              type="email"
              name="email"
              autoComplete="email"
              inputMode="email"
              autoCapitalize="none"
              spellCheck={false}
              placeholder="voce@empresa.com"
              value={form.values.email}
              invalid={form.errors.email !== undefined}
              onChange={(event) => form.setField('email', event.target.value)}
            />
          )}
        </Field>

        <Field label="Senha" error={form.errors.password} required>
          {(control) => (
            <Input
              {...control}
              type="password"
              name="password"
              autoComplete="current-password"
              value={form.values.password}
              invalid={form.errors.password !== undefined}
              onChange={(event) => form.setField('password', event.target.value)}
            />
          )}
        </Field>

        <div className="flex flex-wrap items-center justify-between gap-2">
          <Checkbox
            label="Manter conectado"
            name="rememberMe"
            checked={form.values.rememberMe}
            onChange={(event) => form.setField('rememberMe', event.target.checked)}
          />
          <Link
            to="/esqueci-senha"
            className="text-xs font-medium text-primary-text underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            Esqueci minha senha
          </Link>
        </div>

        {/* ação principal: largura cheia, e o `loading` do DS não muda o tamanho */}
        <Button
          type="submit"
          variant="dark" size="lg"
          block
          loading={form.pending}
          loadingLabel="Entrando"
        >
          Entrar
        </Button>
      </form>
    </AuthLayout>
  );
}
